import type { Question } from "./data"
import { QUESTIONS } from "./questions"

export type ReviewResult = "verified" | "wrong-answer" | "wrong-reference" | "other"
export interface AnswerEvent {
  correct: boolean
  at: number
}

// ============================================================
// REVIEW RESULTS (operator-only)
// Stored as a single aggregate key `reviewResult` whose value is a JSON
// object: { "<id>:v<version>": "<result>", ... }. Embedding the version
// into the cache key scopes records per-question-version: when a question's
// version is bumped the prior entry is silently orphaned and ignored on
// hydrate. Using a single aggregate key keeps the read/write count flat
// regardless of how many questions have results.
//
// All reads hit the in-memory cache. Writes update the cache synchronously
// and fire-and-forget a single set of the aggregate key.
// ============================================================
const REVIEW_RESULT_KEY = "reviewResult"
export const reviewResultCache: Record<string, ReviewResult> = {}

function reviewKey(q: Question) {
  return `${q.id}:v${q.version}`
}

export function getReviewResult(q: Question): ReviewResult | null {
  return reviewResultCache[reviewKey(q)] || null
}

export function setReviewResult(q: Question, result: ReviewResult | null) {
  const ck = reviewKey(q)
  if (result === null) {
    delete reviewResultCache[ck]
  } else {
    reviewResultCache[ck] = result
  }
  persistReviewResults()
}

function persistReviewResults() {
  localStorage.setItem(REVIEW_RESULT_KEY, JSON.stringify(reviewResultCache))
}

export function hydrateReviewResultCache() {
  let aggregate = null
  try {
    const v = localStorage.getItem(REVIEW_RESULT_KEY)
    if (v) aggregate = JSON.parse(v)
  } catch {
    // Aggregate key absent or unreadable. Start with an empty cache.
  }
  if (!aggregate || typeof aggregate !== "object") return
  for (const [ck, result] of Object.entries(aggregate)) {
    const lastColon = ck.lastIndexOf(":v")
    if (lastColon < 0) continue
    const qid = ck.slice(0, lastColon)
    const ver = parseInt(ck.slice(lastColon + 2), 10)
    const q = QUESTIONS.find((x) => x.id === qid)
    if (q && q.version === ver) {
      reviewResultCache[ck] = result as ReviewResult
    }
  }
}

// ============================================================
// BOOKMARKS (user-facing)
// Stored as a single aggregate key `bookmarks`: { "<id>:v<version>": true }.
// Same per-version scoping as review results: bumping a version naturally
// retires any prior bookmark for that question.
// ============================================================
const BOOKMARKS_KEY = "bookmarks"
export const bookmarksCache: Record<string, true> = {}

function bookmarkKey(q: Question) {
  return `${q.id}:v${q.version}`
}

export function isBookmarked(q: Question): boolean {
  return bookmarksCache[bookmarkKey(q)] === true
}

export function toggleBookmark(q: Question) {
  const ck = bookmarkKey(q)
  if (bookmarksCache[ck]) {
    delete bookmarksCache[ck]
  } else {
    bookmarksCache[ck] = true
  }
  persistBookmarks()
}

function persistBookmarks() {
  localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(bookmarksCache))
}

export function hydrateBookmarkCache() {
  let aggregate = null
  try {
    const v = localStorage.getItem(BOOKMARKS_KEY)
    if (v) aggregate = JSON.parse(v)
  } catch {
    // Aggregate key absent or unreadable. Start with an empty cache.
  }
  if (!aggregate || typeof aggregate !== "object") return
  for (const [ck, value] of Object.entries(aggregate)) {
    if (value !== true) continue
    const lastColon = ck.lastIndexOf(":v")
    if (lastColon < 0) continue
    const qid = ck.slice(0, lastColon)
    const ver = parseInt(ck.slice(lastColon + 2), 10)
    const q = QUESTIONS.find((x) => x.id === qid)
    if (q && q.version === ver) {
      bookmarksCache[ck] = true
    }
  }
}

// ============================================================
// ANSWER HISTORY
// Per-question-version event log of {correct, at} entries. Stored as a
// single aggregate key `answerHistory`: { "<id>:v<ver>": [events] }.
// Per-version scoping ensures version bumps naturally orphan prior history.
// ============================================================
const HISTORY_KEY = "answerHistory"
export const historyCache: Record<string, AnswerEvent[]> = {}

export function getHistory(q: Question): AnswerEvent[] {
  return historyCache[`${q.id}:v${q.version}`] || []
}

export function recordAnswer(q: Question, correct: boolean) {
  const ck = `${q.id}:v${q.version}`
  const events = historyCache[ck] || []
  events.push({ correct: !!correct, at: Date.now() })
  historyCache[ck] = events
  persistHistory()
}

function persistHistory() {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(historyCache))
}

export function hydrateHistoryCache() {
  let aggregate = null
  try {
    const v = localStorage.getItem(HISTORY_KEY)
    if (v) aggregate = JSON.parse(v)
  } catch {
    // Aggregate absent or unreadable. Start with an empty cache.
  }
  if (!aggregate || typeof aggregate !== "object") return
  for (const [ck, events] of Object.entries(aggregate)) {
    const lastColon = ck.lastIndexOf(":v")
    if (lastColon < 0) continue
    const qid = ck.slice(0, lastColon)
    const ver = parseInt(ck.slice(lastColon + 2), 10)
    const q = QUESTIONS.find((x) => x.id === qid)
    if (q && q.version === ver && Array.isArray(events)) {
      historyCache[ck] = events as AnswerEvent[]
    }
  }
}
